import React, { useState, useEffect } from "react";
import { X, Search, Send } from "lucide-react";
import toast from "react-hot-toast";
import { shareService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import { labelForStaffRole } from "../../constants/staffRoles";

// Channel picker for the two scout chat cards (design 5d/5e). It lists the user's
// chat recipients (reusing the platform recipient endpoint — a plain HTTP call, no
// Stream connection needed here) and, on send, hands the chosen conversation's
// channel id to `onSend`, which POSTs the share/forward endpoint. The backend does
// the Stream send. The sender must already be a member of the channel; a 403 is
// surfaced calmly ("You're not in this chat yet").
const ScoutShareModal = ({ open, title = "Share into chat", onClose, onSend }) => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState("");
  const debounced = useDebounce(term, 300);
  const [selected, setSelected] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    shareService.getRecipients(debounced).then((res) => {
      if (!alive) return;
      setRecipients(res.success ? res.data || [] : []);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [open, debounced]);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setText("");
      setTerm("");
    }
  }, [open]);

  if (!open) return null;

  const channelIdOf = (r) => r.channel_id_candidate || r.channel_id || null;

  const send = async () => {
    if (!selected) return;
    const channelId = channelIdOf(selected);
    if (!channelId) {
      toast.error("You're not in a chat with this person yet. Open a chat first, then share.");
      return;
    }
    setSending(true);
    const res = await onSend(channelId, text);
    setSending(false);
    if (res?.success) {
      toast.success("Shared into the chat.");
      onClose();
    } else if (res?.status === 403) {
      toast.error("You're not in this chat yet. Open the conversation first, then share.");
    } else {
      toast.error(res?.error || "Could not share into the chat.");
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <h3 className="text-[15px] font-bold text-[#111827]">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="p-1 text-[#9CA3AF] hover:text-[#111827]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search people…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#F3F4F6] text-[14px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {loading ? (
            <p className="text-center text-[13px] text-gray-400 py-8">Loading…</p>
          ) : recipients.length === 0 ? (
            <p className="text-center text-[13px] text-gray-400 py-8">No chats found.</p>
          ) : (
            recipients.map((r) => {
              const id = r.account_id ?? r.id;
              const isSel = selected && (selected.account_id ?? selected.id) === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelected(r)}
                  className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left ${
                    isSel ? "bg-[#E8F1FF]" : "hover:bg-gray-50"
                  }`}
                >
                  <img
                    src={r.profile_picture_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(r.name || "?")}&background=2283FF&color=fff`}
                    alt=""
                    className="w-9 h-9 rounded-full object-cover"
                    onError={(e) => {
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(r.name || "?")}&background=2283FF&color=fff`;
                    }}
                  />
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-[#111827] truncate">{r.name}</p>
                    <p className="text-[11.5px] text-gray-500 truncate">
                      {/* T039: the recipients endpoint sends the raw role_type
                          enum ("assistant_coach"). This picker was missed by the
                          first pass (#240) — run it through the same shared
                          staff-role catalog so every picker reads alike. Values
                          the endpoint already sends humanized ("Agent", "Staff")
                          pass through the catalog's fallback unchanged. */}
                      {(r.role ? labelForStaffRole(r.role) || r.role : null) ||
                        r.agency_name ||
                        r.club_name ||
                        r.email}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>

        <div className="px-5 py-4 border-t border-[#E5E7EB] flex flex-col gap-2.5">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a message (optional)…"
            className="w-full px-3 py-2 rounded-lg bg-[#F3F4F6] text-[13px] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
          />
          <button
            type="button"
            onClick={send}
            disabled={!selected || sending}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#2283FF] text-white text-[13px] font-semibold py-2.5 hover:bg-[#1B6FE0] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {sending ? "Sharing…" : "Share into chat"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoutShareModal;
